(() => {
  const sizeContent = {
    "60x40": { width: 60, height: 40, copyData: "maiSizeCopySmall" },
    "90x60": { width: 90, height: 60, copyData: "maiSizeCopyMedium" },
    "120x80": { width: 120, height: 80, copyData: "maiSizeCopyLarge" },
  };

  const updateMeasurementLabels = (root, size) => {
    const content = sizeContent[size];
    if (!content) return;
    const portrait = root.dataset.artOrientation === "portrait";
    const width = portrait ? content.height : content.width;
    const height = portrait ? content.width : content.height;
    const widthLabel = root.querySelector("[data-mai-dimension-width]");
    const heightLabel = root.querySelector("[data-mai-dimension-height]");
    const unit = root.dataset.maiUnit || "cm";
    if (widthLabel) widthLabel.textContent = `${width} ${unit}`;
    if (heightLabel) heightLabel.textContent = `${height} ${unit}`;
  };

  const waitForImage = async (image) => {
    if (image.complete && image.naturalWidth > 0) return;
    await new Promise((resolve, reject) => {
      image.addEventListener("load", resolve, { once: true });
      image.addEventListener("error", reject, { once: true });
    });
  };

  const fitLine = (points) => {
    if (points.length < 2) return null;
    const totals = points.reduce(
      (sum, point) => ({
        x: sum.x + point.x,
        y: sum.y + point.y,
        xx: sum.xx + point.x * point.x,
        xy: sum.xy + point.x * point.y,
      }),
      { x: 0, y: 0, xx: 0, xy: 0 },
    );
    const denominator = points.length * totals.xx - totals.x * totals.x;
    if (Math.abs(denominator) < 0.0001) return null;
    const slope = (points.length * totals.xy - totals.x * totals.y) / denominator;
    return {
      slope,
      intercept: (totals.y - slope * totals.x) / points.length,
    };
  };

  const fitEdge = (points, tolerance) => {
    let line = fitLine(points);
    if (!line) return null;
    for (let pass = 0; pass < 3; pass += 1) {
      const filtered = points.filter(
        (point) => Math.abs(point.y - (line.slope * point.x + line.intercept)) <= tolerance,
      );
      if (filtered.length < Math.max(12, points.length * 0.4)) break;
      line = fitLine(filtered) || line;
    }
    return line;
  };

  const median = (values) => {
    const ordered = [...values].sort((left, right) => left - right);
    return ordered[Math.floor(ordered.length / 2)] || 0;
  };

  const readScanBackground = (pixels, width, height) => {
    const sampleSize = Math.max(3, Math.round(Math.min(width, height) * 0.025));
    const red = [];
    const green = [];
    const blue = [];
    const samples = [];
    const starts = [
      [0, 0],
      [width - sampleSize, 0],
      [0, height - sampleSize],
      [width - sampleSize, height - sampleSize],
    ];
    starts.forEach(([startX, startY]) => {
      for (let y = startY; y < startY + sampleSize; y += 1) {
        for (let x = startX; x < startX + sampleSize; x += 1) {
          const offset = (y * width + x) * 4;
          const pixel = [pixels[offset], pixels[offset + 1], pixels[offset + 2]];
          red.push(pixel[0]);
          green.push(pixel[1]);
          blue.push(pixel[2]);
          samples.push(pixel);
        }
      }
    });
    const color = [median(red), median(green), median(blue)];
    const deviations = samples.map((pixel) =>
      Math.hypot(pixel[0] - color[0], pixel[1] - color[1], pixel[2] - color[2]),
    );
    return {
      color,
      threshold: Math.max(8, Math.min(20, median(deviations) * 5 + 8)),
    };
  };

  const getMockupProfile = (image) => {
    let filename = "";
    try {
      filename = decodeURIComponent(
        new URL(image.currentSrc || image.src, window.location.href).pathname.split("/").pop() || "",
      ).toLowerCase();
    } catch (_error) {
      filename = (image.currentSrc || image.src || "").toLowerCase();
    }
    const numericMatch = filename.match(/^(\d+)/);
    const numericId = numericMatch ? Number(numericMatch[1]) : null;
    if (/\.jpe?g(?:$|[?_])/i.test(filename)) return "jpeg";
    if (
      (numericId != null && numericId < 10000) ||
      (!/^\d{4,}-m1\.png$/i.test(filename) && /(?:-_1|-m1)\.png$/i.test(filename))
    ) {
      return "legacy-png";
    }
    return "modern-png";
  };

  const isIsolatedCanvasSource = (image) => {
    let filename = "";
    try {
      filename = decodeURIComponent(
        new URL(image.currentSrc || image.src, window.location.href).pathname.split("/").pop() || "",
      ).toLowerCase();
    } catch (_error) {
      filename = (image.currentSrc || image.src || "").toLowerCase();
    }
    return (
      /(?:^|[-_])m1(?:[-_.]|$)/i.test(filename) ||
      /(?:^|[-_])1(?:[-_.]|$)/i.test(filename) ||
      /(?:front|white)/i.test(filename)
    );
  };

  const findArtworkCorners = (image) => {
    const scanSize = 420;
    const scale = Math.min(1, scanSize / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(image, 0, 0, width, height);

    let pixels;
    try {
      pixels = context.getImageData(0, 0, width, height).data;
    } catch (_error) {
      return null;
    }

    const background = readScanBackground(pixels, width, height);
    const isForeground = (offset) => {
      const red = pixels[offset];
      const green = pixels[offset + 1];
      const blue = pixels[offset + 2];
      const alpha = pixels[offset + 3];
      return (
        alpha > 30 &&
        Math.hypot(
          red - background.color[0],
          green - background.color[1],
          blue - background.color[2],
        ) > background.threshold
      );
    };

    const columns = [];
    for (let x = 0; x < width; x += 1) {
      let top = height;
      let bottom = -1;
      let count = 0;
      for (let y = 0; y < height; y += 1) {
        const offset = (y * width + x) * 4;
        if (isForeground(offset)) {
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
          count += 1;
        }
      }
      if (bottom - top > height * 0.28 && count > height * 0.08) {
        columns.push({ x, top, bottom });
      }
    }

    if (columns.length < width * 0.25) return null;
    const xMin = columns[0].x;
    const xMax = columns[columns.length - 1].x;
    const span = xMax - xMin;
    if (span < width * 0.28) return null;

    const edgeColumns = columns.filter(
      (column) => column.x >= xMin + span * 0.025 && column.x <= xMax - span * 0.015,
    );
    const tolerance = Math.max(3, height * 0.018);
    const topLine = fitEdge(
      edgeColumns.map((column) => ({ x: column.x, y: column.top })),
      tolerance,
    );
    const bottomLine = fitEdge(
      edgeColumns.map((column) => ({ x: column.x, y: column.bottom })),
      tolerance,
    );
    if (!topLine || !bottomLine) return null;

    const preliminaryLeft = xMin + span * 0.04;
    const preliminaryRight = xMax - span * 0.008;
    const profile = getMockupProfile(image);
    const point = (x, line) => ({
      x: x / scale,
      y: (line.slope * x + line.intercept) / scale,
    });
    const preliminary = {
      topLeft: point(preliminaryLeft, topLine),
      topRight: point(preliminaryRight, topLine),
      bottomRight: point(preliminaryRight, bottomLine),
      bottomLeft: point(preliminaryLeft, bottomLine),
    };

    const preliminaryRatio =
      (Math.hypot(
        preliminary.topRight.x - preliminary.topLeft.x,
        preliminary.topRight.y - preliminary.topLeft.y,
      ) +
        Math.hypot(
          preliminary.bottomRight.x - preliminary.bottomLeft.x,
          preliminary.bottomRight.y - preliminary.bottomLeft.y,
        )) /
      Math.max(
        1,
        Math.hypot(
          preliminary.bottomLeft.x - preliminary.topLeft.x,
          preliminary.bottomLeft.y - preliminary.topLeft.y,
        ) +
          Math.hypot(
            preliminary.bottomRight.x - preliminary.topRight.x,
            preliminary.bottomRight.y - preliminary.topRight.y,
          ),
      );
    const isLegacyPortrait = profile === "legacy-png" && preliminaryRatio < 1;
    const leftInset =
      profile === "legacy-png" ? (isLegacyPortrait ? 0.095 : 0.08) : 0.04;
    const leftX = xMin + span * leftInset;
    const rightX = xMax - span * (isLegacyPortrait ? 0.018 : 0.008);
    const rawCorners = {
      topLeft: point(leftX, topLine),
      topRight: point(rightX, topLine),
      bottomRight: point(rightX, bottomLine),
      bottomLeft: point(leftX, bottomLine),
    };
    const corners = isLegacyPortrait
      ? {
          topLeft: interpolateQuad(rawCorners, 0, 0.008),
          topRight: interpolateQuad(rawCorners, 1, 0.008),
          bottomRight: interpolateQuad(rawCorners, 1, 0.955),
          bottomLeft: interpolateQuad(rawCorners, 0, 0.955),
        }
      : profile === "jpeg"
        ? {
            topLeft: rawCorners.topLeft,
            topRight: rawCorners.topRight,
            bottomRight: interpolateQuad(rawCorners, 1, 0.98),
            bottomLeft: interpolateQuad(rawCorners, 0, 0.98),
          }
        : rawCorners;

    const topWidth = Math.hypot(
      corners.topRight.x - corners.topLeft.x,
      corners.topRight.y - corners.topLeft.y,
    );
    const bottomWidth = Math.hypot(
      corners.bottomRight.x - corners.bottomLeft.x,
      corners.bottomRight.y - corners.bottomLeft.y,
    );
    const leftHeight = Math.hypot(
      corners.bottomLeft.x - corners.topLeft.x,
      corners.bottomLeft.y - corners.topLeft.y,
    );
    const rightHeight = Math.hypot(
      corners.bottomRight.x - corners.topRight.x,
      corners.bottomRight.y - corners.topRight.y,
    );
    const ratio = (topWidth + bottomWidth) / Math.max(1, leftHeight + rightHeight);
    return { ...corners, ratio, profile };
  };

  const interpolateQuad = (corners, horizontal, vertical) => {
    const top = {
      x: corners.topLeft.x + (corners.topRight.x - corners.topLeft.x) * horizontal,
      y: corners.topLeft.y + (corners.topRight.y - corners.topLeft.y) * horizontal,
    };
    const bottom = {
      x: corners.bottomLeft.x + (corners.bottomRight.x - corners.bottomLeft.x) * horizontal,
      y: corners.bottomLeft.y + (corners.bottomRight.y - corners.bottomLeft.y) * horizontal,
    };
    return {
      x: top.x + (bottom.x - top.x) * vertical,
      y: top.y + (bottom.y - top.y) * vertical,
    };
  };

  const drawMappedTriangle = (context, image, source, destination) => {
    const [sourceA, sourceB, sourceC] = source;
    const [destinationA, destinationB, destinationC] = destination;
    const denominator =
      sourceA.x * (sourceB.y - sourceC.y) +
      sourceB.x * (sourceC.y - sourceA.y) +
      sourceC.x * (sourceA.y - sourceB.y);
    if (Math.abs(denominator) < 0.0001) return;

    const solve = (a, b, c) => ({
      x:
        (a * (sourceB.y - sourceC.y) +
          b * (sourceC.y - sourceA.y) +
          c * (sourceA.y - sourceB.y)) /
        denominator,
      y:
        (a * (sourceC.x - sourceB.x) +
          b * (sourceA.x - sourceC.x) +
          c * (sourceB.x - sourceA.x)) /
        denominator,
      offset:
        (a * (sourceB.x * sourceC.y - sourceC.x * sourceB.y) +
          b * (sourceC.x * sourceA.y - sourceA.x * sourceC.y) +
          c * (sourceA.x * sourceB.y - sourceB.x * sourceA.y)) /
        denominator,
    });
    const horizontal = solve(destinationA.x, destinationB.x, destinationC.x);
    const vertical = solve(destinationA.y, destinationB.y, destinationC.y);

    context.save();
    context.beginPath();
    context.moveTo(destinationA.x, destinationA.y);
    context.lineTo(destinationB.x, destinationB.y);
    context.lineTo(destinationC.x, destinationC.y);
    context.closePath();
    context.clip();
    context.setTransform(
      horizontal.x,
      vertical.x,
      horizontal.y,
      vertical.y,
      horizontal.offset,
      vertical.offset,
    );
    context.drawImage(image, 0, 0);
    context.restore();
  };

  const rectifyArtwork = (image, corners) => {
    const landscape = corners.ratio >= 1;
    const width = landscape ? 1200 : 800;
    const height = landscape ? 800 : 1200;
    const columns = 18;
    const rows = 12;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.fillStyle = "#111";
    context.fillRect(0, 0, width, height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        const u0 = column / columns;
        const u1 = (column + 1) / columns;
        const v0 = row / rows;
        const v1 = (row + 1) / rows;
        const sourceTopLeft = interpolateQuad(corners, u0, v0);
        const sourceTopRight = interpolateQuad(corners, u1, v0);
        const sourceBottomRight = interpolateQuad(corners, u1, v1);
        const sourceBottomLeft = interpolateQuad(corners, u0, v1);
        const destinationTopLeft = { x: u0 * width, y: v0 * height };
        const destinationTopRight = { x: u1 * width, y: v0 * height };
        const destinationBottomRight = { x: u1 * width, y: v1 * height };
        const destinationBottomLeft = { x: u0 * width, y: v1 * height };
        drawMappedTriangle(
          context,
          image,
          [sourceTopLeft, sourceTopRight, sourceBottomRight],
          [destinationTopLeft, destinationTopRight, destinationBottomRight],
        );
        drawMappedTriangle(
          context,
          image,
          [sourceTopLeft, sourceBottomRight, sourceBottomLeft],
          [destinationTopLeft, destinationBottomRight, destinationBottomLeft],
        );
      }
    }
    return { canvas, orientation: landscape ? "landscape" : "portrait" };
  };

  const createArtworkUrl = (canvas) =>
    new Promise((resolve) => {
      if (!canvas.toBlob) {
        resolve(canvas.toDataURL("image/png"));
        return;
      }
      canvas.toBlob(
        (blob) => resolve(blob ? URL.createObjectURL(blob) : canvas.toDataURL("image/png")),
        "image/webp",
        0.94,
      );
    });

  const createMirrorWrapCanvases = (canvas) => {
    const horizontalStrip = Math.max(12, Math.round(canvas.width * 0.075));
    const verticalStrip = Math.max(12, Math.round(canvas.height * 0.075));
    const makeCanvas = (width, height, draw) => {
      const output = document.createElement("canvas");
      output.width = width;
      output.height = height;
      const context = output.getContext("2d");
      if (!context) return output;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      draw(context, output);
      return output;
    };

    return {
      left: makeCanvas(horizontalStrip, canvas.height, (context, output) => {
        context.translate(output.width, 0);
        context.scale(-1, 1);
        context.drawImage(canvas, 0, 0, horizontalStrip, canvas.height, 0, 0, output.width, output.height);
      }),
      right: makeCanvas(horizontalStrip, canvas.height, (context, output) => {
        context.translate(output.width, 0);
        context.scale(-1, 1);
        context.drawImage(
          canvas,
          canvas.width - horizontalStrip,
          0,
          horizontalStrip,
          canvas.height,
          0,
          0,
          output.width,
          output.height,
        );
      }),
      top: makeCanvas(canvas.width, verticalStrip, (context, output) => {
        context.translate(0, output.height);
        context.scale(1, -1);
        context.drawImage(canvas, 0, 0, canvas.width, verticalStrip, 0, 0, output.width, output.height);
      }),
      bottom: makeCanvas(canvas.width, verticalStrip, (context, output) => {
        context.translate(0, output.height);
        context.scale(1, -1);
        context.drawImage(
          canvas,
          0,
          canvas.height - verticalStrip,
          canvas.width,
          verticalStrip,
          0,
          0,
          output.width,
          output.height,
        );
      }),
    };
  };

  const readArtworkBackground = (canvas) => {
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return { color: "rgb(36 36 36)", contrast: "#f5efe5" };
    const { width, height } = canvas;
    const pixels = context.getImageData(0, 0, width, height).data;
    const edgeX = width * 0.12;
    const edgeY = height * 0.12;
    const step = Math.max(4, Math.round(Math.min(width, height) / 120));
    const buckets = new Map();

    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        if (x > edgeX && x < width - edgeX && y > edgeY && y < height - edgeY) continue;
        const offset = (y * width + x) * 4;
        if (pixels[offset + 3] < 200) continue;
        const red = pixels[offset];
        const green = pixels[offset + 1];
        const blue = pixels[offset + 2];
        const key = `${Math.round(red / 24)}-${Math.round(green / 24)}-${Math.round(blue / 24)}`;
        const bucket = buckets.get(key) || { count: 0, red: 0, green: 0, blue: 0 };
        bucket.count += 1;
        bucket.red += red;
        bucket.green += green;
        bucket.blue += blue;
        buckets.set(key, bucket);
      }
    }

    const dominant = [...buckets.values()].sort((a, b) => b.count - a.count)[0];
    if (!dominant) return { color: "rgb(36 36 36)", contrast: "#f5efe5" };
    const red = Math.round(dominant.red / dominant.count);
    const green = Math.round(dominant.green / dominant.count);
    const blue = Math.round(dominant.blue / dominant.count);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
    return {
      color: `rgb(${red} ${green} ${blue})`,
      contrast: luminance > 145 ? "#252321" : "#f5efe5",
    };
  };

  const updateTemplateDeliveryWindow = (scope) => {
    if (!scope) return;
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    const pattern = /Доставка\s*1\s*[-–]\s*3\s*работни\s*дни/giu;
    let node = walker.nextNode();
    while (node) {
      if (pattern.test(node.nodeValue || "")) {
        node.nodeValue = node.nodeValue.replace(pattern, "Доставка 3–5 работни дни");
      }
      pattern.lastIndex = 0;
      node = walker.nextNode();
    }
  };

  const initializeArtwork = async (root) => {
    if (root.dataset.automaticArtExtraction !== "true") return;
    let source = root.querySelector("[data-mai-art-source]");
    const outputs = [...root.querySelectorAll(".mai__art-output")];
    if (!source || outputs.length === 0) return;

    try {
      await waitForImage(source);
      let corners = findArtworkCorners(source);
      if (!corners) throw new Error("Artwork edges were not detected");
      let result = rectifyArtwork(source, corners);
      if (!result) throw new Error("Artwork could not be rectified");

      // Older portrait products keep the isolated canvas in media position 1,
      // while newer products use an explicit m1-white/front image in position 2.
      // Only replace the source after portrait detection so the proven landscape
      // extraction path remains byte-for-byte unchanged.
      if (result.orientation === "portrait" && !isIsolatedCanvasSource(source)) {
        const portraitSource = root.querySelector("[data-mai-art-source-portrait]");
        if (portraitSource && isIsolatedCanvasSource(portraitSource)) {
          await waitForImage(portraitSource);
          const portraitCorners = findArtworkCorners(portraitSource);
          const portraitResult = portraitCorners
            ? rectifyArtwork(portraitSource, portraitCorners)
            : null;
          if (portraitResult?.orientation === "portrait") {
            source = portraitSource;
            corners = portraitCorners;
            result = portraitResult;
          }
        }
      }

      const background = readArtworkBackground(result.canvas);
      const wrapCanvases = createMirrorWrapCanvases(result.canvas);
      const [artworkUrl, leftWrapUrl, rightWrapUrl, topWrapUrl, bottomWrapUrl] = await Promise.all([
        createArtworkUrl(result.canvas),
        createArtworkUrl(wrapCanvases.left),
        createArtworkUrl(wrapCanvases.right),
        createArtworkUrl(wrapCanvases.top),
        createArtworkUrl(wrapCanvases.bottom),
      ]);
      outputs.forEach((output) => {
        output.removeAttribute("srcset");
        output.removeAttribute("sizes");
        output.src = artworkUrl;
        output.dataset.maiExtracted = "true";
      });
      [
        ["left", leftWrapUrl],
        ["right", rightWrapUrl],
        ["top", topWrapUrl],
        ["bottom", bottomWrapUrl],
      ].forEach(([side, url]) => {
        const face = root.querySelector(`.mai__canvas-face--${side}`);
        face?.style.setProperty("--mai-wrap-image", `url("${url}")`);
      });
      root.dataset.artOrientation = result.orientation;
      root.dataset.artExtractionStatus = "ready";
      updateMeasurementLabels(root, root.querySelector("[data-mai-size-room]")?.dataset.previewSize || "60x40");
      root.style.setProperty("--mai-art-background", background.color);
      root.style.setProperty("--mai-art-background-contrast", background.contrast);
      root.classList.add("is-art-ready");
      root._maiArtworkUrl = artworkUrl;
      root._maiArtworkUrls = [artworkUrl, leftWrapUrl, rightWrapUrl, topWrapUrl, bottomWrapUrl];
    } catch (_error) {
      root.dataset.artExtractionStatus = "fallback";
      root.classList.add("is-art-fallback");
    }
  };

  const init = (root) => {
    if (!root || root.dataset.maiReady === "true") return;
    root.dataset.maiReady = "true";
    initializeArtwork(root);

    const productSection = document.querySelector("[id^='ProductSection-']");
    updateTemplateDeliveryWindow(productSection);
    if (productSection && !root._maiDeliveryObserver) {
      root._maiDeliveryObserver = new MutationObserver(() => {
        updateTemplateDeliveryWindow(productSection);
      });
      root._maiDeliveryObserver.observe(productSection, { childList: true, subtree: true });
    }

    const compare = root.querySelector("[data-mai-compare]");
    const compareRange = root.querySelector("[data-mai-compare-range]");
    compareRange?.addEventListener("input", () => {
      compare.style.setProperty("--mai-compare", `${compareRange.value}%`);
    });

    const sizeRoom = root.querySelector("[data-mai-size-room]");
    const sizeArt = root.querySelector("[data-mai-size-art]");
    const sizeLabel = root.querySelector("[data-mai-size-label]");
    const sizeCopy = root.querySelector("[data-mai-size-copy]");
    updateMeasurementLabels(root, sizeRoom?.dataset.previewSize || "60x40");

    root.querySelectorAll("[data-mai-size]").forEach((button) => {
      button.addEventListener("click", () => {
        const size = button.dataset.maiSize;
        const content = sizeContent[size];
        if (!content) return;

        root.querySelectorAll("[data-mai-size]").forEach((candidate) => {
          candidate.classList.toggle("is-active", candidate === button);
        });
        if (sizeRoom) sizeRoom.dataset.previewSize = size;
        if (sizeArt) sizeArt.dataset.previewSize = size;
        const unit = root.dataset.maiUnit || "cm";
        if (sizeLabel) sizeLabel.textContent = `${content.width} × ${content.height} ${unit}`;
        if (sizeCopy) sizeCopy.textContent = root.dataset[content.copyData] || "";
        updateMeasurementLabels(root, size);
      });
    });

    root.querySelectorAll("[data-mai-tone]").forEach((button) => {
      button.addEventListener("click", () => {
        root.querySelectorAll("[data-mai-tone]").forEach((candidate) => {
          candidate.classList.toggle("is-active", candidate === button);
        });
        if (sizeRoom) sizeRoom.dataset.wallTone = button.dataset.maiTone;
      });
    });

    root.querySelectorAll("[data-mai-product-jump]").forEach((button) => {
      button.addEventListener("click", () => {
        const productSection = document.querySelector("[id^='ProductSection-']");
        productSection?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    const stage = root.querySelector("[data-mai-model-stage]");
    const model = root.querySelector("[data-mai-model]");
    const autoButton = root.querySelector("[data-mai-auto-rotate]");
    const resetButton = root.querySelector("[data-mai-reset]");
    if (!stage || !model) return;

    let rotationX = -5;
    let rotationY = -24;
    let dragging = false;
    let pointerX = 0;
    let pointerY = 0;
    let autoRotating = false;
    let frame = 0;

    const render = () => {
      model.style.transform = `rotateX(${rotationX}deg) rotateY(${rotationY}deg)`;
    };

    const stopAuto = () => {
      autoRotating = false;
      model.classList.remove("is-auto-rotating");
      autoButton?.setAttribute("aria-pressed", "false");
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    };

    const rotate = () => {
      if (!autoRotating) return;
      rotationY = (rotationY + 0.28) % 360;
      render();
      frame = window.requestAnimationFrame(rotate);
    };

    stage.addEventListener("pointerdown", (event) => {
      stopAuto();
      dragging = true;
      pointerX = event.clientX;
      pointerY = event.clientY;
      stage.classList.add("is-dragging");
      stage.setPointerCapture?.(event.pointerId);
    });

    stage.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      const deltaX = event.clientX - pointerX;
      const deltaY = event.clientY - pointerY;
      rotationY += deltaX * 0.42;
      rotationX = Math.max(-28, Math.min(28, rotationX - deltaY * 0.3));
      pointerX = event.clientX;
      pointerY = event.clientY;
      render();
    });

    const stopDragging = () => {
      dragging = false;
      stage.classList.remove("is-dragging");
    };
    stage.addEventListener("pointerup", stopDragging);
    stage.addEventListener("pointercancel", stopDragging);

    autoButton?.addEventListener("click", () => {
      if (autoRotating) {
        stopAuto();
        return;
      }
      autoRotating = true;
      model.classList.add("is-auto-rotating");
      autoButton.setAttribute("aria-pressed", "true");
      rotate();
    });

    resetButton?.addEventListener("click", () => {
      stopAuto();
      rotationX = -5;
      rotationY = -24;
      render();
    });

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (root.dataset.autoRotate === "true" && !reduceMotion) {
      autoRotating = true;
      model.classList.add("is-auto-rotating");
      autoButton?.setAttribute("aria-pressed", "true");
      rotate();
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-mai-root]").forEach(init);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
